// AudioRecorder.jsx
import React, { useState, useRef, useEffect } from 'react';
import { Mic, Square, Trash2, Send, Loader2 } from 'lucide-react';

const AudioRecorder = ({ onSend, onCancel }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [duration, setDuration] = useState(0);
  const [permissionError, setPermissionError] = useState(false);
  const [isConverting, setIsConverting] = useState(false);

  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const timerIntervalRef = useRef(null);
  const isMounted = useRef(true);

  // Función para convertir PCM a MP3 usando lamejs
  const convertToMp3 = async (audioBuffer) => {
    // Importar lamejs dinámicamente
    const { Mp3Encoder } = await import('lamejs');
    
    const channels = audioBuffer.numberOfChannels;
    const sampleRate = audioBuffer.sampleRate;
    const samples = audioBuffer.getChannelData(0);
    
    // Convertir de float32 a int16
    const int16Samples = new Int16Array(samples.length);
    for (let i = 0; i < samples.length; i++) {
      int16Samples[i] = Math.max(-32768, Math.min(32767, samples[i] * 32767));
    }
    
    const encoder = new Mp3Encoder(channels, sampleRate, 128);
    const mp3Data = [];
    const sampleBlockSize = 1152;
    
    for (let i = 0; i < int16Samples.length; i += sampleBlockSize) {
      const sampleChunk = int16Samples.subarray(i, i + sampleBlockSize);
      const mp3buf = encoder.encodeBuffer(sampleChunk);
      if (mp3buf.length > 0) {
        mp3Data.push(new Int8Array(mp3buf));
      }
    }
    
    const mp3buf = encoder.flush();
    if (mp3buf.length > 0) {
      mp3Data.push(new Int8Array(mp3buf));
    }
    
    return new Blob(mp3Data, { type: 'audio/mpeg' });
  };

  // Función alternativa para OGG (más compatible)
  const convertToOgg = (webmBlob) => {
    // Para OGG, podemos usar el blob WebM directamente ya que es más compatible
    // o implementar una conversión específica si es necesario
    return new Blob([webmBlob], { type: 'audio/ogg' });
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        } 
      });
      
      if (!isMounted.current) return;
      streamRef.current = stream;
      setPermissionError(false);
      setIsRecording(true);
      setAudioUrl(null);

      // Usar diferentes MIME types según disponibilidad
      let mimeType = 'audio/webm;codecs=opus';
      if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
        mimeType = 'audio/webm;codecs=opus';
      } else if (MediaRecorder.isTypeSupported('audio/webm')) {
        mimeType = 'audio/webm';
      } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
        mimeType = 'audio/mp4';
      }

      const recorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = recorder;
      const audioChunks = [];

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunks.push(event.data);
        }
      };

      recorder.onstop = async () => {
        if (!isMounted.current) return;
        
        streamRef.current?.getTracks().forEach(track => track.stop());
        setIsConverting(true);

        try {
          const webmBlob = new Blob(audioChunks, { type: mimeType });
          
          // Opción 1: Convertir a MP3 (requiere lamejs)
          const audioContext = new (window.AudioContext || window.webkitAudioContext)();
          const arrayBuffer = await webmBlob.arrayBuffer();
          const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
          
          // Intentar convertir a MP3
          let finalBlob;
          let fileName;
          let fileType;
          
          try {
            finalBlob = await convertToMp3(audioBuffer);
            fileName = `nota_de_voz_${new Date().toISOString().split('.')[0].replace(/[:\-]/g, '')}.mp3`;
            fileType = 'audio/mpeg';
          } catch (mp3Error) {
            console.warn('Error al convertir a MP3, usando OGG:', mp3Error);
            // Fallback a OGG
            finalBlob = new Blob([webmBlob], { type: 'audio/ogg' });
            fileName = `nota_de_voz_${new Date().toISOString().split('.')[0].replace(/[:\-]/g, '')}.ogg`;
            fileType = 'audio/ogg';
          }

          const audioFile = new File([finalBlob], fileName, { type: fileType });

          if (isMounted.current) {
            console.log('Archivo creado:', {
              name: audioFile.name,
              type: audioFile.type,
              size: audioFile.size
            });
            onSend(audioFile);
            setAudioUrl(null);
            setDuration(0);
          }

        } catch (error) {
          console.error("Error durante la conversión:", error);
          
          // Fallback: enviar como WebM original
          if (isMounted.current) {
            const webmBlob = new Blob(audioChunks, { type: 'audio/webm' });
            const fileName = `nota_de_voz_${new Date().toISOString().split('.')[0].replace(/[:\-]/g, '')}.webm`;
            const audioFile = new File([webmBlob], fileName, { type: 'audio/webm' });
            
            console.warn('Enviando como WebM debido a error en conversión');
            onSend(audioFile);
          }
        }
        
        if (isMounted.current) {
          setIsConverting(false);
        }
      };

      recorder.start(1000); // Recopilar datos cada segundo
      startTimer();
      
    } catch (error) {
      console.error("Error al acceder al micrófono:", error);
      if (isMounted.current) {
        setPermissionError(true);
        if (onCancel) onCancel();
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      stopTimer();
    }
  };

  const cancelRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
    stopTimer();
    streamRef.current?.getTracks().forEach(track => track.stop());
    if (onCancel) onCancel();
  };

  const startTimer = () => {
    setDuration(0);
    timerIntervalRef.current = setInterval(() => {
      setDuration(prev => prev + 1);
    }, 1000);
  };

  const stopTimer = () => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    startRecording();
    return () => {
      isMounted.current = false;
      stopTimer();
      streamRef.current?.getTracks().forEach(track => track.stop());
    };
  }, []);

  if (permissionError) {
    return (
      <div className="w-full text-center text-red-500 text-sm p-4 bg-red-50 rounded-lg">
        <p>No se pudo acceder al micrófono.</p>
        <p className="text-xs mt-1">Por favor, revisa los permisos del navegador.</p>
      </div>
    );
  }

  if (isConverting) {
    return (
      <div className="w-full flex items-center justify-center gap-x-3 bg-blue-50 p-3 rounded-lg">
        <Loader2 className="animate-spin text-blue-500" size={20} />
        <span className="text-sm text-blue-600">Procesando audio...</span>
      </div>
    );
  }

  return (
    <div className="w-full flex items-center gap-x-3 bg-gray-100 p-3 rounded-lg">
      {isRecording ? (
        <>
          <div className="flex items-center text-red-500">
            <div className="animate-pulse mr-2">
              <Mic size={20} />
            </div>
            <span className="font-mono text-sm">{formatTime(duration)}</span>
          </div>
          
          <div className="flex items-center gap-2 ml-auto">
            <button 
              onClick={cancelRecording} 
              className="text-gray-600 hover:text-gray-800 text-sm px-3 py-1 rounded transition-colors"
            >
              Cancelar
            </button>
            <button 
              onClick={stopRecording} 
              className="bg-red-500 text-white rounded-full h-10 w-10 flex-shrink-0 flex items-center justify-center hover:bg-red-600 transition-colors shadow-md"
            >
              <Square size={16} fill="white" />
            </button>
          </div>
        </>
      ) : (
        <div className="w-full flex items-center justify-center py-2">
          <p className="text-gray-500 text-sm">Grabación finalizada. Enviando...</p>
        </div>
      )}
    </div>
  );
};

export default AudioRecorder;